import { Button } from '@heroui/react';
import { ShieldCheck } from 'lucide-react';
import React, { FC, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DataTable, PageTitle, PageWrapper } from '../../../../components';
import { usePermissions, useWarmup } from '../../../../hooks';
import {
  useGetUsers,
  USERS_FILTER_CONFIG,
  UsersPaginatedFilters,
} from '../../../../queries';
import { StaffClaimsDrawer } from '../../components';
import { UsersColumns } from './columns';

// Strona listy użytkowników na sztywno — 25 rekordów (backend też ma taki default).
const PAGE_SIZE = 25;

export const Users: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? '1');
  const searchTerm = searchParams.get('q') ?? '';
  const filterValues: Record<string, string> = {};

  const filters: UsersPaginatedFilters = { searchTerm };
  // Backfill claimu isStaff — narzędzie administracyjne, tylko dla admina HotelSpot
  // (backend i tak weryfikuje, ale zwykłe konta ACS nie powinny widzieć przycisku).
  const { isAdmin, isHotelspotAdmin } = usePermissions();
  const canManageClaims = isAdmin || isHotelspotAdmin;
  const [showStaffClaims, setShowStaffClaims] = useState(false);

  // Budzimy funkcje v3, z których korzysta ten widok (cold start), zanim
  // użytkownik kliknie usuwanie / ponowną wysyłkę maila.
  useWarmup([
    'v3-getUsers',
    'v3-deleteUser',
    'v3-sendPasswordResetLink',
    'v3-resendWelcomeEmail',
    'v3-backfillStaffClaims',
  ]);

  const { data, isLoading, isError, refetch } = useGetUsers(
    currentPage,
    PAGE_SIZE,
    filters,
  );

  const handleFilterChange = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.set('page', '1');
      return next;
    });
  };

  const handleResetFilters = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', '1');
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set('q', value);
      } else {
        next.delete('q');
      }
      next.set('page', '1');
      return next;
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="Users"
        buttonText="Create user"
        buttonOnClick={() => navigate('/users/new')}
        actions={canManageClaims ? (
          <Button
            size="sm"
            variant="bordered"
            radius="sm"
            startContent={<ShieldCheck className="h-4 w-4" />}
            onPress={() => setShowStaffClaims(true)}
          >
            Uprawnienia personelu
          </Button>
        ) : undefined}
      />
      {showStaffClaims && <StaffClaimsDrawer onClose={() => setShowStaffClaims(false)} />}
      <DataTable
        data={data?.data ?? []}
        columns={UsersColumns}
        isLoading={isLoading}
        isError={isError}
        onRefresh={refetch}
        pageSize={PAGE_SIZE}
        filterableColumns={USERS_FILTER_CONFIG}
        searchPlaceholder="Search by name, surname or e-mail..."
        totalPages={data?.totalPages ?? 1}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
      />
    </PageWrapper>
  );
};
